import { Immutable } from "immer";
import React from "react";
import { useFlickity } from "tsi-common-react/src/hooks/useFlickity";

import type { ComparisonModule as Value } from "@reactivated";

// NOTE: Reproduces the legacy server-rendered markup from
// `cms/product_display_page.html` (the `block_type == 'comparison'` branch)
// verbatim — classnames are load-bearing for the existing SCSS, keep in sync.
type IProps = Immutable<{
    value: Value;
}>;

type ComparisonProduct = NonNullable<Value["comparison_products"]>[number];
type ProductImage = ComparisonProduct["image_a"];

const renderProductImage = (image: ProductImage): React.JSX.Element | null => {
    // Legacy emitted a bare <img src="{{ image.file.url }}"> with no alt.
    // When the image is null we omit the <img> while keeping the wrapper div
    // (rather than rendering a broken `src=""`). `alt=""` (decorative) is added
    // for the alt-less, aria-hidden image to satisfy jsx-a11y; this matches the
    // existing aria-hidden image precedent in tsi-common-react.
    if (!image) {
        return null;
    }
    return (
        <img
            className="pdp-comparison__product__image-item responsive-img"
            src={image.url}
            aria-hidden="true"
            alt=""
            loading="lazy"
            decoding="async"
        />
    );
};

export const ComparisonModule = (props: IProps): React.JSX.Element => {
    const v = props.value;
    const products = v.comparison_products ?? [];
    // watchCSS gates the carousel to the mobile breakpoint.
    const bodyRef = React.useRef<HTMLDivElement>(null);
    useFlickity(bodyRef, {
        options: { watchCSS: true, autoPlay: false },
        enabled: products.length > 0,
        deps: [products.length],
    });

    return (
        <div className="pdp-comparison pdp-section l-capped-width">
            <div className="pdp-comparison__header">
                <h2
                    className="pdp-comparison__title pdp-section__title"
                    dangerouslySetInnerHTML={{
                        __html: v.comparison_title ?? "",
                    }}
                />
                <div
                    className="pdp-comparison__desc pdp-section__desc"
                    dangerouslySetInnerHTML={{
                        __html: v.comparison_description ?? "",
                    }}
                />
            </div>
            {products.length > 0 && (
                <div className="pdp-comparison__body" ref={bodyRef}>
                    {products.map((product, index) => (
                        <div key={index} className="pdp-comparison__container">
                            <div className="pdp-comparison__block">
                                <div className="pdp-comparison__product">
                                    <div className="pdp-comparison__product__image">
                                        {renderProductImage(product.image_a)}
                                    </div>
                                    {/* desc_a/desc_b are NOT `|safe` in legacy
                                        — render as text. The A column always
                                        carries the --left-col modifier. */}
                                    <div className="pdp-comparison__product__desc pdp-comparison__product__desc--left-col">
                                        {product.desc_a}
                                    </div>
                                </div>
                                <hr className="pdp-comparison__product__divider" />
                                <div className="pdp-comparison__product">
                                    <div className="pdp-comparison__product__image">
                                        {renderProductImage(product.image_b)}
                                    </div>
                                    <div className="pdp-comparison__product__desc">
                                        {product.desc_b}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
